import { useEffect, useState } from 'react';
import { differenceInDays, differenceInHours, differenceInMinutes } from 'date-fns';
import type { TimeLeft } from '../types';

/**
 * Hook personalizado para calcular tempo restante até uma data
 * 
 * @param deadline - Data limite para o contador
 * @returns Objeto com dias, horas e minutos restantes. Retorna { days: 0, hours: 0, minutes: 0 } se o deadline passou
 * 
 * @example
 * const timeLeft = useCountdown(new Date('2024-12-31'));
 * // { days: 10, hours: 5, minutes: 30 } ou { days: 0, hours: 0, minutes: 0 } se esgotado
 */
export function useCountdown(deadline?: Date | null): TimeLeft | null {
    const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);

    useEffect(() => {
        if (!deadline) {
            setTimeLeft(null);
            return;
        }

        const updateTime = () => {
            const now = new Date();
            const deadlineDate = new Date(deadline);

            if (deadlineDate <= now) {
                // Quando esgotado, retorna todos zeros
                setTimeLeft({ days: 0, hours: 0, minutes: 0 });
                return;
            }

            // Calcula diferença total em horas e minutos
            const totalHours = differenceInHours(deadlineDate, now);
            const totalMinutes = differenceInMinutes(deadlineDate, now);

            const days = differenceInDays(deadlineDate, now);
            const hours = totalHours % 24;
            const minutes = totalMinutes % 60;

            setTimeLeft({ days, hours, minutes });
        };

        updateTime();
        // Atualiza a cada segundo para animação mais suave
        const interval = setInterval(updateTime, 1000);

        return () => clearInterval(interval);
    }, [deadline]);

    return timeLeft;
}

